import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { useTenantStore } from '@core/tenant/useTenantStore';
import { ThemeType } from '@core/generated/gql-types';
import { useThemeStore } from '@core/theme/useThemeStore';
import { TenantInfo } from '@core/tenant/tenant.action';
import { resolveAppContext } from '@core/portal/clientPortal.util';
import { AppContext } from '@core/type/types';

interface TenantContextType {
  tenantId: string | null;
  tenantInfo: TenantInfo | null;
  logoUrl: string | null;
  enableClientPortal: boolean;
  isLoading: boolean;
  error: string | null;
  refreshTenant: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantInfo, setTenantInfo] = useState<TenantInfo | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [enableClientPortal, setEnableClientPortal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getCurrentTenantInfo } = useTenantStore();
  const { fetchTheme } = useThemeStore();

  const refreshTenant = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Get tenant info and resolution using useTenantStore
      const { tenantInfo: info, resolution } = await getCurrentTenantInfo();

      if (!info) {
        // No tenant in domain (e.g., base domain like app.insta-agency.com or staging-app.insta-agency.com)
        // This is a valid state - proceed without tenant assignment
        setTenantId(null);
        setTenantInfo(null);
        setEnableClientPortal(true);
        setError(null);
        setIsLoading(false);
        return;
      }

      if (resolution && resolution.success) {
        setTenantId(resolution.tenantId);
        setError(null);
        // Set logoUrl from tenant resolution (takes priority)
        setLogoUrl(resolution.logoUrl || null);
        setEnableClientPortal(resolution.enableClientPortal ?? false);

        // Use resolution data, merging with info to preserve fullDomain
        setTenantInfo({
          tenantName: resolution.tenantName,
          environment: resolution.environment,
          fullDomain: info.fullDomain,
        });

        // Fetch theme for the resolved tenant
        const portalAppContext = resolveAppContext();
        await fetchTheme(
          resolution.tenantId,
          portalAppContext === AppContext.ClientApp
            ? ThemeType.ClientWeb
            : ThemeType.TenantWeb,
        );
      } else {
        // Fallback to info if resolution failed
        setTenantInfo(info);
        setError(resolution?.error || 'Failed to resolve tenant ID');
        setTenantId(null);
        setLogoUrl(null);
        setEnableClientPortal(true);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      setTenantId(null);
      setLogoUrl(null);
      setEnableClientPortal(true);
      console.error('Error refreshing tenant:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void refreshTenant();
  }, []);

  const value: TenantContextType = {
    tenantId,
    tenantInfo,
    logoUrl,
    enableClientPortal,
    isLoading,
    error,
    refreshTenant,
  };

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
};

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

// Hook for getting just the tenant ID (most common use case)
export const useTenantId = (): string | null => {
  const { tenantId } = useTenant();
  return tenantId;
};

// Hook for getting tenant info without ID resolution
export const useTenantInfo = (): TenantInfo | null => {
  const { tenantInfo } = useTenant();
  return tenantInfo;
};

// Hook for getting tenant logo URL
export const useTenantLogoUrl = (): string | null => {
  const { logoUrl } = useTenant();
  return logoUrl;
};
